import type { LocationQuery } from 'vue-router'

import type {
  AffiliateCompetenceItem,
  AffiliateCompetencePeriod,
  AffiliateMeResponse
} from '@/Network/Types/Responses/AntecipeAffiliateResponses'

import { formatDayMonth, monthRange } from './format'

/**
 * Regras de EXIBIÇÃO da competência do afiliado (spec 2026-10-03 §5). A regra do ciclo
 * (dia de início, período, fechada) é do backend: aqui só se lê a URL, monta opções e
 * escreve texto com o que a API manda (`periodStart`/`periodEnd`/`closed`).
 */

export const COMPETENCE_QUERY_KEY = 'competencia'

const COMPETENCE_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/

const SHORT_MONTHS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez'
]

export type CompetenceBounds = Pick<AffiliateMeResponse, 'firstCompetence' | 'openCompetence'>

export interface CompetenceOption {
  value: string
  label: string
}

export const isCompetenceKey = (value: unknown): value is string =>
  typeof value === 'string' && COMPETENCE_PATTERN.test(value)

const firstValue = (value: LocationQuery[string] | undefined): unknown =>
  Array.isArray(value) ? value[0] : value

/** `?competencia=` dentro de [primeira, aberta]; qualquer outra coisa cai na aberta. */
export function resolveCompetence(query: LocationQuery, bounds: CompetenceBounds): string {
  const raw = firstValue(query[COMPETENCE_QUERY_KEY])
  if (isCompetenceKey(raw) && raw >= bounds.firstCompetence && raw <= bounds.openCompetence) {
    return raw
  }
  return bounds.openCompetence
}

/** Sem parâmetro = aberta: a aberta nunca é escrita na URL. */
export const competenceQuery = (
  competence: string,
  bounds: CompetenceBounds
): Record<string, string> =>
  competence === bounds.openCompetence ? {} : { [COMPETENCE_QUERY_KEY]: competence }

/** Leva o `?competencia=` da rota atual (só formato válido) para os links internos. */
export function preservedCompetenceQuery(query: LocationQuery): Record<string, string> {
  const raw = firstValue(query[COMPETENCE_QUERY_KEY])
  return isCompetenceKey(raw) ? { [COMPETENCE_QUERY_KEY]: raw } : {}
}

/** Chaves da aberta até a primeira (decrescente); primeira inválida → só a aberta. */
export const competenceKeys = (bounds: CompetenceBounds): string[] =>
  isCompetenceKey(bounds.firstCompetence) && bounds.firstCompetence <= bounds.openCompetence
    ? monthRange(bounds.firstCompetence, bounds.openCompetence)
    : [bounds.openCompetence]

/** `2026-10` → `out/2026`. */
export const formatCompetenceShort = (key: string): string => {
  const [year, month] = key.split('-').map(Number)
  return `${SHORT_MONTHS[month - 1]}/${year}`
}

/** `01/10–31/10` (en dash). */
export const formatCompetencePeriod = (periodStart: string, periodEnd: string): string =>
  `${formatDayMonth(periodStart)}–${formatDayMonth(periodEnd)}`

/** `out/2026 · 01/10–31/10`. */
export const formatCompetenceTitle = (
  period: Pick<AffiliateCompetencePeriod, 'competence' | 'periodStart' | 'periodEnd'>
): string =>
  `${formatCompetenceShort(period.competence)} · ${formatCompetencePeriod(period.periodStart, period.periodEnd)}`

export const competenceStatusHint = (
  period: Pick<AffiliateCompetencePeriod, 'closed' | 'periodEnd'>
): string =>
  period.closed
    ? `Competência fechada em ${formatDayMonth(period.periodEnd)}.`
    : `Competência em aberto até ${formatDayMonth(period.periodEnd)}. Os valores podem mudar até o fechamento.`

/**
 * Opções do seletor (spec §5): chaves do `/me`; período do item de `/competences` quando já
 * chegou. Sem item, toda chave anterior à aberta é fechada (o fim dela é anterior ao
 * início da aberta) — nenhuma regra de ciclo no web.
 */
export function buildCompetenceOptions(
  bounds: CompetenceBounds,
  items: AffiliateCompetenceItem[] | null
): CompetenceOption[] {
  const byKey = new Map((items ?? []).map((item) => [item.competence, item]))
  return competenceKeys(bounds).map((key) => {
    const item = byKey.get(key)
    const closed = item ? item.closed : key !== bounds.openCompetence
    const parts = [formatCompetenceShort(key)]
    if (item) parts.push(formatCompetencePeriod(item.periodStart, item.periodEnd))
    parts.push(closed ? 'fechada' : 'aberta')
    return { value: key, label: parts.join(' · ') }
  })
}
